import { Controller } from "@hotwired/stimulus"

// Public contract-signing page. Signature adoption works like DocuSign:
// Type (script-style preview of the typed name), Draw (canvas pad), or
// Upload (PNG/JPG). Drawn/uploaded signatures post as a data-URL image in
// the hidden signature_data field; the Sign button stays disabled until
// name + consent + a valid signature for the picked style are in place.
export default class extends Controller {
  static targets = ["name", "agree", "submit", "attentionNote", "declineBox", "signModal",
                    "kindField", "dataField", "kindTab",
                    "typedPanel", "typedPreview", "drawnPanel", "pad",
                    "uploadedPanel", "uploadInput", "uploadPreviewBox", "uploadPreview", "uploadError",
                    "docEnd", "scrollNotice", "scrollNoticeText", "signTrigger"]

  connect() {
    this._kind = "typed"
    this._padDirty = false
    this._uploadData = null
    if (this.hasPadTarget) this._initPad()
    this.refreshPreview()
    this.validate()
    this._watchDocEnd()
  }

  disconnect() {
    this._endObserver?.disconnect()
  }

  // ── Read-the-whole-thing gate (Phelipe 2026-08-06) ────────────────────
  // The page itself scrolls, so "Review & sign" unlocks once the end of the
  // agreement — wording AND fee schedule — has actually come into view. A
  // short contract that fits on screen unlocks immediately.
  _watchDocEnd() {
    if (!this.hasDocEndTarget || !this.hasSignTriggerTarget) return

    // Only the controller ever disables it, so a JS failure leaves the button
    // usable rather than stranding the signer.
    this.signTriggerTarget.disabled = true
    if (!("IntersectionObserver" in window)) return this._unlockSigning()

    this._endObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return

      this._unlockSigning()
      this._endObserver.disconnect()
    })
    this._endObserver.observe(this.docEndTarget)
  }

  _unlockSigning() {
    this.signTriggerTarget.disabled = false
    if (!this.hasScrollNoticeTarget) return

    this.scrollNoticeTarget.className =
      "mx-auto max-w-[680px] mt-3 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 " +
      "text-[13px] text-green-800 flex items-center gap-2"
    if (this.hasScrollNoticeTextTarget) {
      this.scrollNoticeTextTarget.textContent =
        this.scrollNoticeTextTarget.dataset.readText || "You've reached the end — you can sign now."
    }
  }

  // ── Adopt-signature modal (opened from the yellow "Sign here" tab) ──
  openModal(e) {
    if (e) e.preventDefault()
    if (this.hasSignModalTarget) this.signModalTarget.classList.remove("hidden")
    this.refreshPreview()
    this.validate()
  }
  closeModal(e) {
    if (e) e.preventDefault()
    if (this.hasSignModalTarget) this.signModalTarget.classList.add("hidden")
  }
  stopModal(e) { if (e) e.stopPropagation() }

  // ── Signature style tabs ──
  pickKind(e) {
    this._kind = e.currentTarget.dataset.kind
    if (this.hasKindFieldTarget) this.kindFieldTarget.value = this._kind
    this.kindTabTargets.forEach(tab => {
      const on = tab.dataset.kind === this._kind
      tab.classList.toggle("bg-white", on)
      tab.classList.toggle("shadow-sm", on)
      tab.classList.toggle("font-medium", on)
      tab.classList.toggle("text-gray-800", on)
      tab.classList.toggle("text-gray-600", !on)
    })
    if (this.hasTypedPanelTarget) this.typedPanelTarget.classList.toggle("hidden", this._kind !== "typed")
    if (this.hasDrawnPanelTarget) this.drawnPanelTarget.classList.toggle("hidden", this._kind !== "drawn")
    if (this.hasUploadedPanelTarget) this.uploadedPanelTarget.classList.toggle("hidden", this._kind !== "uploaded")
    this.validate()
  }

  refreshPreview() {
    if (this.hasTypedPreviewTarget && this.hasNameTarget) {
      this.typedPreviewTarget.textContent = this.nameTarget.value.trim() || "Your name"
    }
  }

  // ── Draw pad ──
  _initPad() {
    const canvas = this.padTarget
    const ctx = canvas.getContext("2d")
    ctx.lineWidth = 2.2
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.strokeStyle = "#1f2937"
    let drawing = false

    const pos = (e) => {
      const r = canvas.getBoundingClientRect()
      return [(e.clientX - r.left) * (canvas.width / r.width),
              (e.clientY - r.top) * (canvas.height / r.height)]
    }
    canvas.addEventListener("pointerdown", (e) => {
      e.preventDefault()
      drawing = true
      canvas.setPointerCapture(e.pointerId)
      ctx.beginPath()
      ctx.moveTo(...pos(e))
    })
    canvas.addEventListener("pointermove", (e) => {
      if (!drawing) return
      e.preventDefault()
      ctx.lineTo(...pos(e))
      ctx.stroke()
      if (!this._padDirty) { this._padDirty = true; this.validate() }
    })
    const stop = () => { drawing = false }
    canvas.addEventListener("pointerup", stop)
    canvas.addEventListener("pointercancel", stop)
  }

  clearPad() {
    if (!this.hasPadTarget) return
    const ctx = this.padTarget.getContext("2d")
    ctx.clearRect(0, 0, this.padTarget.width, this.padTarget.height)
    this._padDirty = false
    this.validate()
  }

  // ── Upload ──
  uploadPicked(e) {
    const file = e.currentTarget.files?.[0]
    this._uploadData = null
    if (this.hasUploadErrorTarget) this.uploadErrorTarget.classList.add("hidden")
    if (this.hasUploadPreviewBoxTarget) this.uploadPreviewBoxTarget.classList.add("hidden")
    if (!file) { this.validate(); return }

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      this._uploadFail("That file isn't a PNG or JPG image.")
      return
    }
    if (file.size > 1024 * 1024) {
      this._uploadFail("That image is over 1 MB — please use a smaller one.")
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      this._uploadData = reader.result
      if (this.hasUploadPreviewTarget) this.uploadPreviewTarget.src = this._uploadData
      if (this.hasUploadPreviewBoxTarget) this.uploadPreviewBoxTarget.classList.remove("hidden")
      this.validate()
    }
    reader.readAsDataURL(file)
  }

  _uploadFail(msg) {
    if (this.hasUploadInputTarget) this.uploadInputTarget.value = ""
    if (this.hasUploadErrorTarget) {
      this.uploadErrorTarget.textContent = msg
      this.uploadErrorTarget.classList.remove("hidden")
    }
    this.validate()
  }

  // Fill signature_data right before the form posts.
  prepareSubmit() {
    if (!this.hasDataFieldTarget) return
    if (this._kind === "drawn" && this._padDirty) {
      this.dataFieldTarget.value = this.padTarget.toDataURL("image/png")
    } else if (this._kind === "uploaded" && this._uploadData) {
      this.dataFieldTarget.value = this._uploadData
    } else {
      this.dataFieldTarget.value = ""
    }
  }

  validate() {
    if (!this.hasSubmitTarget) return
    const nameOk = this.hasNameTarget && this.nameTarget.value.trim() !== ""
    const agreeOk = this.hasAgreeTarget && this.agreeTarget.checked
    const signatureOk =
      this._kind === "typed" ? true :
      this._kind === "drawn" ? this._padDirty :
      !!this._uploadData
    // Flagged report items (Larry 2026-09-08): every "needs attention" box
    // on the page has to be ticked before the signature can go through.
    const checks = [...document.querySelectorAll("[data-attention-check]")]
    const left = checks.filter(c => !c.checked).length
    if (this.hasAttentionNoteTarget) {
      this.attentionNoteTarget.textContent = left ? `Review the ${left} flagged item${left === 1 ? "" : "s"} on the page first — tick each one, then sign.` : ""
      this.attentionNoteTarget.classList.toggle("hidden", !left)
    }
    this.submitTarget.disabled = !(nameOk && agreeOk && signatureOk && left === 0)
  }

  toggleDecline() {
    if (this.hasDeclineBoxTarget) this.declineBoxTarget.classList.toggle("hidden")
  }
}
